import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { teamsService } from '../../../services/supabaseService';

const TeamStatsPanel = ({ team, onClose }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamTournaments();
  }, [team?.id]);

  const fetchTeamTournaments = async () => {
    setLoading(true);
    try {
      const data = await teamsService?.getTournamentHistory(team?.id);
      setTournaments(data || []);
    } catch (error) {
      console.error('Error fetching tournament history:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1200]">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Team Statistics</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="X" className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Team Info */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-4">
              <img
                src={team?.logo || 'https://img.rocket.new/generatedImages/rocket_gen_img_16b616325-1766600649637.png'}
                alt={team?.logoAlt || `${team?.name} logo`}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h3 className="text-lg font-semibold text-foreground">{team?.name}</h3>
                <p className="text-sm text-muted-foreground">{team?.country}</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Tournaments</p>
              <p className="text-2xl font-bold text-foreground mt-1">{team?.tournamentCount || 0}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Players</p>
              <p className="text-2xl font-bold text-foreground mt-1">{team?.playerCount || 0}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg col-span-2 md:col-span-1">
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="text-lg font-semibold text-green-600 mt-1">Active</p>
            </div>
          </div>

          {/* Tournament History */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Tournament Participation</h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Loading tournaments...</p>
              </div>
            ) : tournaments?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="Trophy" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No tournament participation yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tournaments?.map((tournament) => (
                  <div
                    key={tournament?.id}
                    className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-foreground">{tournament?.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {tournament?.format} • {tournament?.matchType}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(tournament?.startDate)?.toLocaleDateString()} - {new Date(tournament?.endDate)?.toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        tournament?.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        tournament?.status === 'upcoming' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        tournament?.status === 'completed'? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {tournament?.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamStatsPanel;